import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cashboxApi, canDoCashbox, type CashboxOperation } from '../../../lib/cashboxApi.js';
import { useRole } from '../../../lib/role.tsx';
import { Icon } from '../../../components/Icon.js';
import { OperationModal } from './OperationModal.js';

const TYPE_ICON: Record<string, string> = { cash: '💵', transfer: '💳', europe: '🇪🇺', debt: '📝' };
const TYPE_LABEL: Record<string, string> = { cash: 'Наличные', transfer: 'Безнал', europe: 'Европа', debt: 'Долг' };

export function CashboxOperations() {
  const role = useRole();
  const qc = useQueryClient();
  const canCreate = canDoCashbox(role, 'create');
  const canDeleteAny = canDoCashbox(role, 'delete_any');
  const canSync = canDoCashbox(role, 'update_own');

  const [showModal, setShowModal] = useState(false);
  const [editOp, setEditOp] = useState<CashboxOperation | undefined>(undefined);
  const [filterType, setFilterType] = useState('');
  const [filterDir, setFilterDir] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const params: Record<string, string | undefined> = {};
  if (filterType) params['type'] = filterType;
  if (filterDir) params['direction'] = filterDir;
  if (from) params['from'] = from;
  if (to) params['to'] = to;

  const { data, isLoading } = useQuery({
    queryKey: ['cashbox', 'operations', params],
    queryFn: () => cashboxApi.getOperations(params),
  });

  const softDelete = useMutation({
    mutationFn: (id: string) => cashboxApi.softDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cashbox'] }),
  });

  const syncAmo = useMutation({
    mutationFn: (id: string) => cashboxApi.syncAmo(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cashbox', 'operations'] });
      qc.invalidateQueries({ queryKey: ['cashbox', 'audit'] });
    },
  });

  const ops = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div>
      {/* Filter bar */}
      <div className="toolbar" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <select
          className="input-admin"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={{ width: 130 }}
          data-testid="filter-type"
        >
          <option value="">Все типы</option>
          <option value="cash">💵 Нал</option>
          <option value="transfer">💳 Безнал</option>
          <option value="europe">🇪🇺 Европа</option>
        </select>
        <select
          className="input-admin"
          value={filterDir}
          onChange={(e) => setFilterDir(e.target.value)}
          style={{ width: 130 }}
        >
          <option value="">Все направления</option>
          <option value="income">Приход</option>
          <option value="expense">Расход</option>
        </select>
        <input className="input-admin" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <input className="input-admin" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        <div className="grow" />
        {canCreate && (
          <button
            className="btn-admin primary"
            onClick={() => { setEditOp(undefined); setShowModal(true); }}
            data-testid="cb-ops-new-btn"
          >
            <Icon name="plus" size={14} /> Новая операция
          </button>
        )}
      </div>

      <div className="panel">
        <div className="panel-head">
          <p className="panel-title">Операции {total > 0 && <span className="muted">({total})</span>}</p>
        </div>
        {isLoading && (
          <p style={{ padding: 32, textAlign: 'center', color: 'var(--mnp-fg-3)' }}>Загрузка…</p>
        )}
        {!isLoading && (
          <table className="data" data-testid="operations-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Дата</th>
                <th>Тип</th>
                <th>Счёт</th>
                <th>Треки</th>
                <th>AMO</th>
                <th className="num">Сумма GEL</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {ops.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--mnp-fg-3)' }}>
                    Операций нет
                  </td>
                </tr>
              )}
              {ops.map((op) => (
                <tr key={op.id}>
                  <td className="mono" style={{ fontSize: 11 }}>{op.id}</td>
                  <td className="muted">{new Date(op.operatedAt).toLocaleDateString('ru-RU')}</td>
                  <td>
                    <span className={`cb-type cb-type-${op.type}`}>
                      {TYPE_ICON[op.type]} {TYPE_LABEL[op.type]}
                    </span>
                  </td>
                  <td className="muted">{op.opType}{op.account ? ` · ${op.account}` : ''}</td>
                  <td className="muted" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {op.tracks.join(', ')}
                  </td>
                  <td>
                    <AmoStatusBadge op={op} onSync={canSync ? () => syncAmo.mutate(op.id) : undefined} />
                  </td>
                  <td className={`num tnum bold ${op.direction === 'income' ? 'cb-income' : 'cb-expense'}`}>
                    {op.direction === 'income' ? '+' : '−'}{Math.abs(Math.round(op.amountGel)).toLocaleString('ru-RU')}
                  </td>
                  <td>
                    <div className="f-row" style={{ gap: 4 }}>
                      <button
                        className="btn-admin sm"
                        onClick={() => { setEditOp(op); setShowModal(true); }}
                        title="Редактировать"
                      >
                        <Icon name="edit" size={12} />
                      </button>
                      {canDeleteAny && (
                        <button
                          className="btn-admin sm danger"
                          onClick={() => {
                            if (confirm('Удалить операцию?')) softDelete.mutate(op.id);
                          }}
                          title="Удалить"
                        >
                          <Icon name="trash" size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <OperationModal
          operation={editOp}
          onClose={() => { setShowModal(false); setEditOp(undefined); }}
        />
      )}
    </div>
  );
}

function AmoStatusBadge({ op, onSync }: { op: CashboxOperation; onSync?: () => void }) {
  const status = op.amoStatus;
  const url = op.amoLeadUrl;
  if (!status) {
    // Нет синка, но ссылка на сделку уже известна — даём перейти в AmoCRM.
    if (url) return <AmoDealLink url={url} label="AMO" />;
    return <span className="muted">—</span>;
  }
  const statuses = Object.values(status);
  const hasError = statuses.some((s) => s.status === 'error');
  const allOk = statuses.every((s) => s.status === 'ok');

  if (allOk) {
    if (url) return <AmoDealLink url={url} label="✓ AMO" className="green" />;
    return <span className="badge-a green">✓ AMO</span>;
  }
  if (hasError && onSync) {
    return (
      <span className="f-row" style={{ gap: 4, alignItems: 'center' }}>
        <button className="badge-a red" style={{ cursor: 'pointer', border: 'none' }} onClick={onSync}>
          🔄 Повторить
        </button>
        {url && <AmoDealLinkIcon url={url} />}
      </span>
    );
  }
  if (hasError) {
    if (url) return <AmoDealLink url={url} label="⚠ AMO" className="red" />;
    return <span className="badge-a red">⚠ AMO</span>;
  }
  if (url) return <AmoDealLink url={url} label="⏳ AMO" className="yellow" />;
  return <span className="badge-a yellow">⏳ AMO</span>;
}

function AmoDealLink({ url, label, className }: { url: string; label: string; className?: string }) {
  return (
    <a
      className={`badge-a${className ? ` ${className}` : ''}`}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title="Открыть сделку в AmoCRM"
      style={{ textDecoration: 'none' }}
    >
      {label} ↗
    </a>
  );
}

function AmoDealLinkIcon({ url }: { url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title="Открыть сделку в AmoCRM"
      style={{ textDecoration: 'none' }}
    >
      ↗
    </a>
  );
}
